/**
 * Post Journal Entry — month-end adjusting entries from /admin.
 *
 * Posts through /api/quickbooks-journal (requireAdmin). The account list is
 * the qb_accounts cache plus any other active QuickBooks account that cache
 * does not store, so a WIP deferral can pick an asset account. Preview
 * validates and checks for a duplicate journal number without writing.
 */
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  surface: "#13161b",
  border: "#1e2329",
  text: "#e8ecf0",
  muted: "#8a919c",
  green: "#22c55e",
  red: "#ef4444",
  yellow: "#f59e0b",
  blue: "#60a5fa",
};

const input = {
  padding: "6px 10px",
  borderRadius: 6,
  border: `1px solid ${C.border}`,
  background: "#0d0f12",
  color: C.text,
  fontSize: 13,
};

const btn = (active, color = C.blue) => ({
  padding: "6px 12px",
  borderRadius: 6,
  border: `1px solid ${active ? color : C.border}`,
  background: active ? "rgba(96,165,250,0.1)" : "transparent",
  color: active ? color : C.muted,
  fontSize: 12,
  fontWeight: 600,
  cursor: active ? "pointer" : "not-allowed",
  opacity: active ? 1 : 0.55,
});

function todayIso() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Vancouver", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

let lineKey = 1;
const blankLine = (side) => ({ key: lineKey++, accountId: "", side, amount: "", description: "", filter: "" });

function money(cents) {
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function lineCents(line, side) {
  if (line.side !== side) return 0;
  const n = Number(line.amount);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function grouped(accounts, line) {
  const q = line.filter.trim().toLowerCase();
  const rows = accounts.filter((a) => {
    if (String(a.id) === String(line.accountId)) return true;
    if (!q) return true;
    return `${a.name} ${a.account_type || ""} ${a.id}`.toLowerCase().includes(q);
  });
  const groups = new Map();
  for (const a of rows) {
    const g = a.account_type || "Other";
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(a);
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

export default function PostJournalEntry() {
  const [txnDate, setTxnDate] = useState(todayIso);
  const [docNumber, setDocNumber] = useState("");
  const [memo, setMemo] = useState("");
  const [reverseOn, setReverseOn] = useState("");
  const [lines, setLines] = useState(() => [blankLine("Debit"), blankLine("Credit")]);
  const [accounts, setAccounts] = useState([]);
  const [accountNote, setAccountNote] = useState(null);
  const [accountError, setAccountError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch("/api/quickbooks-journal", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "accounts" }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data.ok === false) throw new Error(data.error || `HTTP ${r.status}`);
        if (cancelled) return;
        setAccounts(data.accounts || []);
        const extra = data.fromQbo
          ? `${data.fromCache} from the qb_accounts cache, plus ${data.fromQbo} other QuickBooks account${data.fromQbo === 1 ? "" : "s"} (assets, liabilities, and the rest of the chart).`
          : `${data.fromCache || 0} accounts from the qb_accounts cache.`;
        setAccountNote(data.warning ? `${data.warning} ${extra}` : extra);
      } catch (e) {
        if (!cancelled) setAccountError(e.message || "Could not load accounts");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const nameOf = (id) => accounts.find((a) => String(a.id) === String(id))?.name || id;

  const debitCents = lines.reduce((s, l) => s + lineCents(l, "Debit"), 0);
  const creditCents = lines.reduce((s, l) => s + lineCents(l, "Credit"), 0);
  const diff = debitCents - creditCents;
  const complete = lines.length >= 2 && lines.every((l) => l.accountId && Number(l.amount) > 0);
  const balanced = complete && diff === 0;

  const spec = useMemo(() => ({
    txnDate,
    docNumber: docNumber.trim(),
    privateNote: memo.trim(),
    reverseOn: reverseOn || null,
    lines: lines.map((l) => ({
      accountId: l.accountId,
      postingType: l.side,
      amount: l.amount === "" ? "" : Number(l.amount),
      description: l.description.trim(),
    })),
  }), [txnDate, docNumber, memo, reverseOn, lines]);
  const specKey = JSON.stringify(spec);
  const previewMatches = preview && preview.key === specKey && preview.data?.ok;

  function patchLine(key, patch) {
    setLines((rows) => rows.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setResult(null);
  }

  async function call(body) {
    const r = await apiFetch("/api/quickbooks-journal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    return { status: r.status, data };
  }

  async function onPreview() {
    setBusy("preview"); setError(null); setResult(null);
    try {
      const { data } = await call({ action: "postJournalEntry", dryRun: true, ...spec });
      setPreview({ key: specKey, data });
      if (data.ok === false) setError(data.error || "Preview failed");
    } catch (e) {
      setError(e.message || "Preview failed");
    } finally {
      setBusy(null);
    }
  }

  async function onPost() {
    const rev = reverseOn ? ` and reversing entry ${docNumber.trim()}-R on ${reverseOn}` : "";
    if (!window.confirm(`Post journal entry ${docNumber.trim()} dated ${txnDate}${rev} to QuickBooks?`)) return;
    setBusy("post"); setError(null);
    try {
      const { data } = await call({ action: "postJournalEntry", dryRun: false, ...spec });
      setResult(data);
      setPreview(null);
      if (data.ok === false) setError(data.error || "Post failed");
    } catch (e) {
      setError(e.message || "Post failed");
    } finally {
      setBusy(null);
    }
  }

  function EntryBlock({ entry, title }) {
    if (!entry) return null;
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
          {title} · {entry.DocNumber} · {entry.TxnDate}
        </div>
        {entry.PrivateNote && (
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{entry.PrivateNote}</div>
        )}
        {(entry.Line || []).map((l, i) => {
          const d = l.JournalEntryLineDetail || {};
          const id = d.AccountRef?.value;
          return (
            <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, marginTop: 4, color: C.text }}>
              <span style={{ width: 52, color: d.PostingType === "Debit" ? C.blue : C.yellow }}>{d.PostingType}</span>
              <span style={{ flex: 1 }}>{nameOf(id)}</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>${Number(l.Amount).toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  function IdLink({ href, label }) {
    if (!href) return <span>{label}</span>;
    return <a href={href} target="_blank" rel="noreferrer" style={{ color: C.blue }}>{label}</a>;
  }

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>
        Post Journal Entry
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
          Posts a general journal entry in QuickBooks. Use it for month-end adjustments,
          for example Oct 31 WIP: debit 1210 Job Materials WIP, credit 5015 Job Materials - COS.
          An optional <strong style={{ color: C.text }}>Reverse on</strong> date posts a second entry
          with debits and credits swapped (QuickBooks has no auto-reverse flag). The journal number
          is suffixed <span style={{ color: C.text }}>-R</span>.
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: C.muted }}>
            Date
            <input type="date" value={txnDate} onChange={(e) => { setTxnDate(e.target.value); setResult(null); }} style={{ ...input, display: "block", marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 11, color: C.muted, flex: "1 1 140px" }}>
            Journal no.
            <input
              value={docNumber}
              maxLength={reverseOn ? 19 : 21}
              onChange={(e) => { setDocNumber(e.target.value); setResult(null); }}
              placeholder={reverseOn ? "Max 19 chars" : "Max 21 chars"}
              style={{ ...input, display: "block", marginTop: 4, width: "100%" }}
            />
          </label>
          <label style={{ fontSize: 11, color: C.muted }}>
            Reverse on
            <input type="date" value={reverseOn} onChange={(e) => { setReverseOn(e.target.value); setResult(null); }} style={{ ...input, display: "block", marginTop: 4 }} />
          </label>
        </div>
        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 12 }}>
          Memo
          <input
            value={memo}
            onChange={(e) => { setMemo(e.target.value); setResult(null); }}
            placeholder="Oct 31 WIP deferral"
            style={{ ...input, display: "block", marginTop: 4, width: "100%" }}
          />
        </label>

        {accountError && (
          <div style={{ marginBottom: 10, fontSize: 12, color: C.red }}>{accountError}</div>
        )}
        {accountNote && !accountError && (
          <div style={{ marginBottom: 10, fontSize: 11, color: C.muted }}>{accountNote}</div>
        )}

        {lines.map((line) => (
          <div key={line.key} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            <input
              value={line.filter}
              onChange={(e) => patchLine(line.key, { filter: e.target.value })}
              placeholder="Find account"
              style={{ ...input, width: 110 }}
            />
            <select
              value={line.accountId}
              onChange={(e) => patchLine(line.key, { accountId: e.target.value })}
              style={{ ...input, flex: "1 1 220px" }}
            >
              <option value="">Account…</option>
              {grouped(accounts, line).map(([type, rows]) => (
                <optgroup key={type} label={type}>
                  {rows.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </optgroup>
              ))}
            </select>
            <select
              value={line.side}
              onChange={(e) => patchLine(line.key, { side: e.target.value })}
              style={{ ...input, width: 90 }}
            >
              <option>Debit</option>
              <option>Credit</option>
            </select>
            <input
              type="number"
              min="0"
              step="0.01"
              value={line.amount}
              onChange={(e) => patchLine(line.key, { amount: e.target.value })}
              placeholder="0.00"
              style={{ ...input, width: 100 }}
            />
            <input
              value={line.description}
              onChange={(e) => patchLine(line.key, { description: e.target.value })}
              placeholder="Description"
              style={{ ...input, flex: "1 1 160px" }}
            />
            <button
              onClick={() => { setLines((rows) => rows.filter((l) => l.key !== line.key)); setResult(null); }}
              disabled={lines.length <= 2}
              style={btn(lines.length > 2, C.red)}
            >
              Remove
            </button>
          </div>
        ))}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
          <button onClick={() => { setLines((rows) => [...rows, blankLine("Debit")]); setResult(null); }} style={btn(true)}>
            Add line
          </button>
          <div style={{ fontSize: 12, fontWeight: 700, color: balanced ? C.green : C.yellow }}>
            Debits {money(debitCents)} · Credits {money(creditCents)}
            {balanced
              ? " · In balance"
              : diff === 0
                ? " · Needs two complete lines"
                : ` · Out of balance by ${money(diff)}`}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button onClick={onPreview} disabled={!!busy} style={btn(!busy, C.yellow)}>
            {busy === "preview" ? "Checking…" : "Preview"}
          </button>
          <button onClick={onPost} disabled={!previewMatches || !!busy} style={btn(!!previewMatches && !busy, C.green)}>
            {busy === "post" ? "Posting…" : "Post"}
          </button>
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 6, background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}`, color: C.red, fontSize: 12 }}>
            {error}
          </div>
        )}

        {previewMatches && (
          <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 6, background: "rgba(245,158,11,0.08)", border: `1px solid ${C.yellow}`, fontSize: 12, color: C.text }}>
            Preview only — nothing written to QuickBooks.
            {preview.data.alreadyPosted?.length > 0 && (
              <div style={{ color: C.yellow, marginTop: 4 }}>
                Already in QuickBooks (will not be created again): {preview.data.alreadyPosted.join(", ")}
              </div>
            )}
            <EntryBlock entry={preview.data.entry} title="Would post" />
            <EntryBlock entry={preview.data.reversal} title="Would reverse" />
          </div>
        )}

        {result?.journalEntryId && (
          <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 6, background: "rgba(34,197,94,0.08)", border: `1px solid ${result.ok ? C.green : C.yellow}`, fontSize: 12, color: C.text }}>
            <div>
              <IdLink href={result.links?.journal} label={`${result.docNumber || docNumber} · JE ${result.journalEntryId}`} />
              {result.postedBy ? ` · logged as ${result.postedBy}` : ""}
            </div>
            {result.reversalJournalEntryId && (
              <div style={{ marginTop: 4 }}>
                <IdLink href={result.links?.reversal} label={`${result.reversalDocNumber || `${docNumber}-R`} · JE ${result.reversalJournalEntryId}`} />
              </div>
            )}
            {result.logError && (
              <div style={{ marginTop: 4, color: C.yellow }}>Posted, but the audit log write failed: {result.logError}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
